/** React Query hooks — the only place the UI talks to the API. */
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  CoachMessage,
  DayTotals,
  DayView,
  Entry,
  EntryInput,
  FoodSearchResult,
  FoodView,
  HistoryFood,
  OnboardInput,
  ParseResult,
  SessionView,
  SettingsView,
  UsualMeal,
  WeeklyRecap,
  WeightEntry,
  WeightInsight,
  WeightView,
  WhatFitsResult,
} from '../../shared/api';
import type { ISODate, Meal } from '../../shared/dates';
import { scaleMap, sumWithCoverage } from '../../shared/nutrients';
import { api } from './client';

export const qk = {
  session: ['session'] as const,
  settings: ['settings'] as const,
  day: (date: ISODate) => ['day', date] as const,
  days: (from: ISODate, to: ISODate) => ['days', from, to] as const,
  weights: (range: string, today: ISODate) => ['weights', range, today] as const,
  history: (kind: 'recent' | 'frequent', meal: Meal | null) => ['history', kind, meal] as const,
  favorites: ['favorites'] as const,
  customFoods: ['customFoods'] as const,
  usual: (date: ISODate, meal: Meal) => ['usual', date, meal] as const,
  insight: (today: ISODate) => ['insight', today] as const,
  recap: (today: ISODate) => ['recap', today] as const,
  coach: ['coach'] as const,
};

// ——— Sign-in

export function useSession() {
  return useQuery({ queryKey: qk.session, queryFn: () => api<SessionView>('/auth/session'), staleTime: 5 * 60_000, retry: 1 });
}

/**
 * After signing in or out: update the session in place (the gate is watching it),
 * then drop every other cached query, since they hold personal data.
 */
export function useApplySession() {
  const client = useQueryClient();
  return (session: SessionView) => {
    client.setQueryData(qk.session, session);
    client.removeQueries({ predicate: (q) => q.queryKey[0] !== qk.session[0] });
  };
}

export function useSignOut() {
  const apply = useApplySession();
  return useMutation({
    mutationFn: (opts: { everywhere?: boolean } = {}) => api<SessionView>(opts.everywhere ? '/auth/logout-all' : '/auth/logout', { method: 'POST', body: {} }),
    onSuccess: apply,
  });
}

// ——— Queries

export function useSettings() {
  return useQuery({ queryKey: qk.settings, queryFn: () => api<SettingsView>('/settings'), staleTime: 60_000 });
}

export function useDay(date: ISODate, today: ISODate) {
  return useQuery({
    queryKey: qk.day(date),
    queryFn: () => api<DayView>(`/days/${date}?today=${today}`),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function prefetchDay(client: QueryClient, date: ISODate, today: ISODate) {
  return client.prefetchQuery({ queryKey: qk.day(date), queryFn: () => api<DayView>(`/days/${date}?today=${today}`), staleTime: 15_000 });
}

export function useDays(from: ISODate, to: ISODate) {
  return useQuery({
    queryKey: qk.days(from, to),
    queryFn: () => api<DayTotals[]>(`/days?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useWeights(range: string, today: ISODate) {
  return useQuery({
    queryKey: qk.weights(range, today),
    queryFn: () => api<WeightView>(`/weights?range=${range}&today=${today}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useHistory(kind: 'recent' | 'frequent', meal: Meal | null, enabled = true) {
  return useQuery({
    queryKey: qk.history(kind, meal),
    queryFn: () => api<HistoryFood[]>(`/foods/history?kind=${kind}${meal ? `&meal=${meal}` : ''}`),
    enabled,
    staleTime: 30_000,
  });
}

export function useFavorites(enabled = true) {
  return useQuery({ queryKey: qk.favorites, queryFn: () => api<HistoryFood[]>('/foods/favorites'), enabled, staleTime: 30_000 });
}

export function useCustomFoods() {
  return useQuery({ queryKey: qk.customFoods, queryFn: () => api<FoodView[]>('/foods/custom'), staleTime: 60_000 });
}

/** Server-side search (FatSecret when configured). Callers debounce the query. */
export function useFoodDbSearch(query: string, enabled: boolean) {
  const q = query.trim();
  return useQuery({
    queryKey: ['foodSearch', q.toLowerCase()],
    queryFn: ({ signal }) => api<FoodSearchResult>(`/foods/search?q=${encodeURIComponent(q)}`, { signal }),
    enabled: enabled && q.length >= 2,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Full details (servings, per-100 g nutrients) for any food ref; cached by React Query. */
export function fetchFoodItem(client: QueryClient, ref: string) {
  return client.fetchQuery({
    queryKey: ['foodItem', ref],
    queryFn: () => api<FoodView>(`/foods/item/${encodeURIComponent(ref)}`),
    staleTime: 30 * 60_000,
  });
}

export function useUsual(date: ISODate, meal: Meal) {
  return useQuery({
    queryKey: qk.usual(date, meal),
    queryFn: () => api<UsualMeal[]>(`/suggestions/usual?date=${date}&meal=${meal}`),
    staleTime: 60_000,
  });
}

export function useWeightInsight(today: ISODate, enabled: boolean) {
  return useQuery({
    queryKey: qk.insight(today),
    queryFn: () => api<{ insight: WeightInsight | null }>(`/ai/weight-insight?today=${today}`),
    enabled,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useWeeklyRecap(today: ISODate, enabled: boolean) {
  return useQuery({
    queryKey: qk.recap(today),
    queryFn: () => api<{ recap: WeeklyRecap | null }>(`/ai/weekly-recap?today=${today}`),
    enabled,
    staleTime: 60 * 60_000,
    retry: false,
  });
}

export function useCoachMessages() {
  return useQuery({ queryKey: qk.coach, queryFn: () => api<CoachMessage[]>('/coach/messages'), staleTime: Infinity });
}

// ——— Entry mutations (optimistic where it makes the UI feel instant)

function recomputeDay(day: DayView, entries: Entry[]): DayView {
  const scaled = entries.map((e) => scaleMap(e.per100, e.grams));
  const { totals, known } = sumWithCoverage(scaled);
  return {
    ...day,
    totals,
    known,
    entryCount: entries.length,
    meals: day.meals.map((m) => {
      const list = entries.filter((e) => e.meal === m.meal);
      return { ...m, entries: list, totals: sumWithCoverage(list.map((e) => scaleMap(e.per100, e.grams))).totals };
    }),
  };
}

/** Refetch everything food-related; resolves once the visible queries have reloaded. */
function refetchFood(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['day'] }),
    client.invalidateQueries({ queryKey: ['days'] }),
    client.invalidateQueries({ queryKey: ['history'] }),
    client.invalidateQueries({ queryKey: ['usual'] }),
    client.invalidateQueries({ queryKey: qk.favorites }),
  ]);
}

function invalidateFood(client: QueryClient) {
  void refetchFood(client);
}

export function useAddEntries() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entries: EntryInput[]) => api<Entry[]>('/entries', { method: 'POST', body: { entries } }),
    onSuccess: (created) => {
      for (const date of new Set(created.map((e) => e.date))) {
        client.setQueryData<DayView>(qk.day(date), (day) =>
          day ? recomputeDay(day, [...day.meals.flatMap((m) => m.entries), ...created.filter((e) => e.date === date)]) : day,
        );
      }
      invalidateFood(client);
    },
  });
}

export function useUpdateEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; date: ISODate; patch: { grams?: number; meal?: Meal; date?: ISODate } }) =>
      api<Entry>(`/entries/${id}`, { method: 'PATCH', body: patch }),
    onMutate: async ({ id, date, patch }) => {
      await client.cancelQueries({ queryKey: qk.day(date) });
      const previous = client.getQueryData<DayView>(qk.day(date));
      if (previous) {
        const entries = previous.meals
          .flatMap((m) => m.entries)
          .map((e) => (e.id === id ? { ...e, ...patch } : e))
          .filter((e) => e.date === date);
        client.setQueryData(qk.day(date), recomputeDay(previous, entries));
      }
      return { previous, date };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) client.setQueryData(qk.day(ctx.date), ctx.previous);
    },
    onSettled: () => invalidateFood(client),
  });
}

export function useDeleteEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; date: ISODate }) => api<void>(`/entries/${id}`, { method: 'DELETE' }),
    onMutate: async ({ id, date }) => {
      await client.cancelQueries({ queryKey: qk.day(date) });
      const previous = client.getQueryData<DayView>(qk.day(date));
      if (previous) {
        client.setQueryData(
          qk.day(date),
          recomputeDay(
            previous,
            previous.meals.flatMap((m) => m.entries).filter((e) => e.id !== id),
          ),
        );
      }
      return { previous, date };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) client.setQueryData(qk.day(ctx.date), ctx.previous);
    },
    onSettled: () => invalidateFood(client),
  });
}

export function useCopyEntries() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { fromDate: ISODate; toDate: ISODate; meal?: Meal }) =>
      api<Entry[]>('/entries/copy', { method: 'POST', body: input }),
    // Stay pending until the day reloads, so "Copy yesterday" can't run twice.
    onSuccess: () => refetchFood(client),
  });
}

// ——— Foods

export function useCreateCustomFood() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      brand?: string | null;
      servingLabel: string;
      servingGrams: number;
      perServing: Record<string, number>;
      liquid?: boolean;
    }) => api<FoodView>('/foods/custom', { method: 'POST', body: input }),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.customFoods }),
  });
}

export function useToggleFavorite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ ref, entryId, on }: { ref?: string; entryId?: string; on: boolean }) => {
      if (on) return api<{ ref: string }>('/foods/favorites', { method: 'POST', body: { ref, entryId } });
      if (!ref) return null;
      await api<void>(`/foods/favorites/${encodeURIComponent(ref)}`, { method: 'DELETE' });
      return null;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.favorites });
      void client.invalidateQueries({ queryKey: ['history'] });
      void client.invalidateQueries({ queryKey: qk.customFoods });
      void client.invalidateQueries({ queryKey: ['day'] });
    },
  });
}

// ——— Weight

function invalidateWeight(client: QueryClient) {
  void client.invalidateQueries({ queryKey: ['weights'] });
  void client.invalidateQueries({ queryKey: qk.settings });
  void client.invalidateQueries({ queryKey: ['day'] });
  void client.invalidateQueries({ queryKey: ['insight'] });
}

export function useSaveWeight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id?: string; date: ISODate; kg: number; note?: string | null }) =>
      id
        ? api<WeightEntry>(`/weights/${id}`, { method: 'PATCH', body: input })
        : api<WeightEntry>('/weights', { method: 'POST', body: input }),
    onSuccess: () => invalidateWeight(client),
  });
}

export function useDeleteWeight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/weights/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateWeight(client),
  });
}

// ——— Settings

function useSettingsMutation<TInput>(path: string, method: 'PATCH' | 'PUT' | 'POST' | 'DELETE', toPath?: (input: TInput) => string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      api<SettingsView>(toPath ? toPath(input) : path, { method, body: method === 'DELETE' ? undefined : input }),
    onSuccess: (settings) => {
      client.setQueryData(qk.settings, settings);
      void client.invalidateQueries({ queryKey: ['day'] });
      void client.invalidateQueries({ queryKey: ['weights'] });
      void client.invalidateQueries({ queryKey: ['insight'] });
      void client.invalidateQueries({ queryKey: ['recap'] });
    },
  });
}

export const useUpdateProfile = () => useSettingsMutation<Record<string, unknown>>('/settings/profile', 'PATCH');
export const useUpdateGoals = () =>
  useSettingsMutation<{ mode: 'suggested' | 'custom'; kcal?: number; protein?: number; carbs?: number; fat?: number }>('/settings/goals', 'PUT');
export const useUpdateTracking = () =>
  useSettingsMutation<{ items: Array<{ key: string; tracked: boolean; target: number | null; direction: 'min' | 'max' | 'target' }> }>(
    '/settings/tracking',
    'PUT',
  );
export const useCreateNutrient = () =>
  useSettingsMutation<{ name: string; unit: string; direction: 'min' | 'max' | 'target'; target: number | null }>('/settings/nutrients', 'POST');
export const useDeleteNutrient = () =>
  useSettingsMutation<string>('', 'DELETE', (key) => `/settings/nutrients/${encodeURIComponent(key)}`);

export function useOnboard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: OnboardInput) => api<SettingsView>('/account/onboard', { method: 'POST', body: input }),
    onSuccess: (settings) => {
      client.setQueryData(qk.settings, settings);
      void client.invalidateQueries();
    },
  });
}

export function useResetAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<SettingsView>('/account/reset', { method: 'POST', body: { confirm: 'erase' } }),
    onSuccess: (settings) => {
      client.clear();
      client.setQueryData(qk.settings, settings);
    },
  });
}

export function useResetDemo() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (today: ISODate) => api<{ weights: number; entries: number }>('/demo/reset', { method: 'POST', body: { today } }),
    onSuccess: () => client.invalidateQueries(),
  });
}

// ——— AI

export function useParseFood() {
  return useMutation({
    mutationFn: (input: {
      date: ISODate;
      meal?: Meal;
      mode: 'meal' | 'label';
      text?: string;
      image?: { mimeType: 'image/jpeg'; base64: string };
      hour: number;
    }) => api<ParseResult>('/ai/parse', { method: 'POST', body: input }),
  });
}

export function useWhatFits() {
  return useMutation({
    mutationFn: (input: {
      date: ISODate;
      meal?: Meal;
      mode: 'mine' | 'new' | 'menu';
      menuText?: string;
      menuImage?: { mimeType: 'image/jpeg'; base64: string };
      hour: number;
    }) => api<WhatFitsResult>('/ai/what-fits', { method: 'POST', body: input }),
  });
}

export function useRefreshInsight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (today: ISODate) => api<{ insight: WeightInsight | null }>('/ai/weight-insight/refresh', { method: 'POST', body: { today } }),
    onSuccess: (data, today) => client.setQueryData(qk.insight(today), data),
  });
}

export function useRefreshRecap() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (today: ISODate) => api<{ recap: WeeklyRecap | null }>('/ai/weekly-recap/refresh', { method: 'POST', body: { today } }),
    onSuccess: (data, today) => client.setQueryData(qk.recap(today), data),
  });
}

export function useClearCoach() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/coach/messages', { method: 'DELETE' }),
    onSuccess: () => client.setQueryData(qk.coach, []),
  });
}
